import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { EditorHeader } from "./EditorHeader";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const PipelineEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<string>(editingItem?.type || "pipeline");

  const { data: items, refetch: refetchItems } = useQuery({
    queryKey: ['pipeline-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pipeline_items')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const validateFormData = (formData: FormData) => {
    const type = String(formData.get('type'));
    const name = String(formData.get('name'));
    const value = Number(formData.get('value'));

    if (!name || !value || !type) {
      throw new Error("Name, value, and type are required fields");
    }

    if (type === 'pipeline') {
      const probability = formData.get('probability');
      const stage = formData.get('stage');
      const expected_close = formData.get('expected_close');

      if (!probability || !stage || !expected_close) {
        throw new Error("Probability, stage, and expected close date are required for pipeline items");
      }
    }

    if (type === 'backlog') {
      const priority = formData.get('priority');
      if (!priority) {
        throw new Error("Priority is required for backlog items");
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      validateFormData(formData);

      const data = {
        name: String(formData.get('name')),
        value: Number(formData.get('value')),
        type: String(formData.get('type')),
        probability: formData.get('probability') ? Number(formData.get('probability')) : null,
        stage: formData.get('stage') ? String(formData.get('stage')) : null,
        expected_close: formData.get('expected_close') ? String(formData.get('expected_close')) : null,
        priority: formData.get('priority') ? String(formData.get('priority')) : null,
      };

      let error;
      
      if (editingItem?.id) {
        ({ error } = await supabase
          .from('pipeline_items')
          .update(data)
          .eq('id', editingItem.id));
      } else {
        ({ error } = await supabase
          .from('pipeline_items')
          .insert([data]));
      }

      if (error) throw error;

      toast.success(editingItem?.id 
        ? "Pipeline item updated successfully"
        : "Pipeline item added successfully"
      );
      
      setEditingItem(null);
      refetchItems();
      e.currentTarget.reset();
      setSelectedType("pipeline");
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(error.message || "Failed to save pipeline item");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setSelectedType(item.type);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setSelectedType("pipeline");
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('pipeline_items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success("Pipeline item deleted successfully");
      refetchItems();
    } catch (error) {
      console.error('Error:', error);
      toast.error("Failed to delete pipeline item");
    }
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
  };

  return (
    <div className="space-y-6">
      <EditorHeader 
        title="Pipeline Editor"
        description="Add and manage pipeline items and backlog opportunities"
      />
      
      <EditorForm onSubmit={handleSubmit} isSubmitting={isSubmitting}>
        <div className="space-y-4">
          <EditorField
            label="Name"
            name="name"
            type="text"
            required
            defaultValue={editingItem?.name}
          />
          <EditorField
            label="Value"
            name="value"
            type="number"
            required
            defaultValue={editingItem?.value}
          />
          <div className="space-y-2">
            <label className="text-sm font-medium">Type</label>
            <Select
              name="type"
              value={selectedType}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pipeline">Pipeline</SelectItem>
                <SelectItem value="backlog">Backlog</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedType === "pipeline" && (
            <>
              <EditorField
                label="Probability (%)"
                name="probability"
                type="number"
                required
                defaultValue={editingItem?.probability}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Stage</label>
                <Select
                  name="stage"
                  defaultValue={editingItem?.stage}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="negotiation">Negotiation</SelectItem>
                    <SelectItem value="proposal">Proposal</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <EditorField
                label="Expected Close"
                name="expected_close"
                type="text"
                required
                defaultValue={editingItem?.expected_close}
              />
            </>
          )}

          {selectedType === "backlog" && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                name="priority"
                defaultValue={editingItem?.priority}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {editingItem && (
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
            className="mt-2"
          >
            Cancel Edit
          </Button>
        )}
      </EditorForm>

      <div className="mt-8 space-y-4">
        {items?.map((item) => (
          <Card key={item.id} className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium">{item.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Value: ${(item.value / 1000000).toFixed(1)}M | Type: {item.type}
                </p>
                {item.type === 'pipeline' && (
                  <p className="text-sm text-muted-foreground">
                    Stage: {item.stage} | Probability: {item.probability}% | Expected Close: {item.expected_close}
                  </p>
                )}
                {item.type === 'backlog' && item.priority && (
                  <p className="text-sm text-muted-foreground">
                    Priority: {item.priority}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(item)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};